// ═══════════════════════════════════════════════════════
// OdooSync — Synchronisation Odoo simplifiée
// Plus de formulaire de credentials — tout est côté serveur
// ═══════════════════════════════════════════════════════

import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';

const SYNC_URL = '/.netlify/functions/odoo-sync-trigger';
const LAST_SYNC_KEY = 'elixir_last_sync';

export default function OdooSync({ open, onClose, onSynced }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    if (open) {
      setStatus(null);
      const saved = localStorage.getItem(LAST_SYNC_KEY);
      if (saved) setLastSync(new Date(saved));
    }
  }, [open]);

  // Nettoyage des anciennes clés Odoo dans localStorage
  useEffect(() => {
    ['elixir_prep_odoo_creds', 'elixir_prep_odoo_url', 'elixir_prep_odoo_db',
     'elixir_prep_odoo_login', 'elixir_prep_odoo_password'].forEach(k => {
      if (localStorage.getItem(k)) {
        localStorage.removeItem(k);
        console.log('[security] Supprimé clé localStorage:', k);
      }
    });
  }, []);

  async function handleSync() {
    setLoading(true);
    setStatus({ type: 'info', msg: 'Synchronisation en cours…' });

    try {
      const res = await fetch(SYNC_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const json = await res.json().catch(() => ({}));

      if (res.status === 202) {
        setStatus({ type: 'ok', msg: json.result?.message || 'Synchronisation lancée.' });
        localStorage.setItem(LAST_SYNC_KEY, new Date().toISOString());
        setLastSync(new Date());
        // Rafraîchir après un délai pour laisser le worker tourner
        setTimeout(() => { onSynced(); onClose(); }, 4000);
      } else if (res.status === 409) {
        setStatus({ type: 'info', msg: json.result?.message || 'Une synchronisation est déjà en cours.' });
      } else if (res.status === 401) {
        setStatus({ type: 'err', msg: 'Session expirée. Reconnecte-toi.' });
      } else {
        setStatus({ type: 'err', msg: 'Erreur ' + res.status + ': ' + (json.error || 'échec du lancement') });
      }
    } catch (e) {
      setStatus({ type: 'err', msg: 'Erreur réseau: ' + e.message });
    } finally {
      setLoading(false);
    }
  }

  if (!open) return null;

  const modal = (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={e => e.stopPropagation()}>
        <div style={S.header}>
          <span style={S.headerTitle}>Synchroniser Odoo</span>
          <button style={S.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={S.body}>
          <div style={{ textAlign: 'center', padding: '8px 0 16px' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🔄</div>
            <div style={{ fontSize: 14, fontWeight: 600, color: '#1A1D23', marginBottom: 4 }}>
              Récupérer les commandes depuis Odoo
            </div>
            <div style={{ fontSize: 12, color: '#6B7280', lineHeight: 1.5 }}>
              La synchronisation automatique tourne toutes les 5 minutes.
              <br />Utilise ce bouton si tu veux forcer une synchro maintenant.
            </div>
          </div>

          {lastSync && (
            <div style={{ fontSize: 11, color: '#9CA3AF', textAlign: 'center', marginBottom: 12 }}>
              Dernière synchro manuelle : {lastSync.toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
            </div>
          )}

          {status && (
            <div style={{
              padding: '10px 12px', borderRadius: 8, fontSize: 12, fontWeight: 500, lineHeight: 1.4, marginBottom: 8,
              background: status.type === 'err' ? '#FDECEA' : status.type === 'ok' ? '#D5F5E3' : '#EEF6FF',
              color: status.type === 'err' ? '#C0392B' : status.type === 'ok' ? '#1E6B3C' : '#1A5276',
            }}>{status.msg}</div>
          )}
        </div>
        <div style={S.footer}>
          <button style={S.cancelBtn} onClick={onClose}>Fermer</button>
          <button style={{ ...S.syncBtn, opacity: loading ? 0.6 : 1 }} onClick={handleSync} disabled={loading}>
            {loading ? '⏳ En cours…' : '🔄 Synchroniser maintenant'}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}

const S = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 9998, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 },
  modal: { background: 'white', borderRadius: 16, width: '100%', maxWidth: 400, overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' },
  header: { background: '#18181A', padding: '14px 18px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  headerTitle: { color: 'white', fontSize: 14, fontWeight: 700 },
  closeBtn: { background: 'none', border: 'none', color: '#888', fontSize: 18, cursor: 'pointer' },
  body: { padding: '16px 18px' },
  footer: { padding: '12px 18px', background: '#FAFAFA', borderTop: '1px solid #E8E8EE', display: 'flex', justifyContent: 'flex-end', gap: 8 },
  cancelBtn: { padding: '8px 16px', fontSize: 13, fontWeight: 600, border: '1.5px solid #E2E2E8', borderRadius: 8, background: 'white', cursor: 'pointer', fontFamily: 'inherit', color: '#18181A' },
  syncBtn: { padding: '8px 16px', fontSize: 13, fontWeight: 600, border: 'none', borderRadius: 8, background: '#1A5276', color: 'white', cursor: 'pointer', fontFamily: 'inherit' },
};
